/**
 * SefariaReaderModal Component
 *
 * Full-screen Torah reader with:
 * - Cache-first data loading
 * - Multiple layout modes
 * - Verse highlighting
 * - Expandable commentary
 * - Reading position persistence
 * - Offline support
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useMountedRef } from '@/hooks/useMountedRef';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader, AlertCircle, RefreshCw } from 'lucide-react';
import { VerseView } from './VerseView';
import { CommentaryPanel } from './CommentaryPanel';
import { ReaderHeader, ReaderFooter } from './ReaderToolbar';
import { ReaderSettingsPanel } from './ReaderSettingsPanel';
import {
  getParshaText,
  getImmediateParshaText,
  isParshaAvailableOffline,
  getAvailableVersions,
} from '@/services/sefariaTextService';
import {
  getReaderSettings,
  setReaderSettings,
  getHighlights,
  toggleHighlight,
  clearHighlights,
  getReadingPosition,
  setReadingPosition,
  clearReadingPosition,
} from '@/services/sefariaCache';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import { useVisualEffectsContext } from '@/context/VisualEffectsContext';
import { useReadingProgress } from '@/hooks/useReadingProgress';
import { useBodyOverflowLock } from '@/hooks/useBodyOverflowLock';
import { formatCelebrationMessage } from '@/types/reading';
import type {
  SefariaReaderModalProps,
  SefariaText,
  SefariaVersion,
  ReaderLayout,
  ReaderFontSize,
} from '@/types/sefaria';

// ============================================================
// Hooks
// ============================================================

/**
 * Hook for online/offline status
 */
function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}

/**
 * Hook for Sefaria text with cache-first loading
 */
function useSefariaText(parshaRef: string, parshaName?: string) {
  // Initialize with cached data immediately
  const [text, setText] = useState<SefariaText | null>(() => getImmediateParshaText(parshaRef));
  const [loading, setLoading] = useState(!text);
  const [error, setError] = useState<Error | null>(null);
  const [isFromCache, setIsFromCache] = useState(!!text);

  const hasFetched = useRef(false);
  const currentRef = useRef(parshaRef);

  useEffect(() => {
    // Reset if ref changed
    if (currentRef.current !== parshaRef) {
      currentRef.current = parshaRef;
      hasFetched.current = false;
      const cached = getImmediateParshaText(parshaRef);
      setText(cached);
      setIsFromCache(!!cached);
      if (cached) {
        setLoading(false);
        return;
      }
    }

    // Skip if already fetched or have cached data
    if (hasFetched.current || text) {
      setLoading(false);
      return;
    }

    hasFetched.current = true;
    setLoading(true);

    getParshaText(parshaRef, { parshaName })
      .then((data) => {
        setText(data);
        setError(null);
        setIsFromCache(false);
      })
      .catch((err) => {
        setError(err);
        // Try stale cache as fallback
        const stale = getImmediateParshaText(parshaRef);
        if (stale) {
          setText(stale);
          setIsFromCache(true);
        }
      })
      .finally(() => setLoading(false));
  }, [parshaRef, parshaName, text]);

  const refresh = useCallback(() => {
    hasFetched.current = false;
    setLoading(true);
    setError(null);

    getParshaText(parshaRef, { parshaName, forceRefresh: true })
      .then((data) => {
        setText(data);
        setIsFromCache(false);
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }, [parshaRef, parshaName]);

  return { text, loading, error, isFromCache, refresh };
}

// ============================================================
// Main Component
// ============================================================

export function SefariaReaderModal({
  parshaRef,
  parshaTitle,
  parshaHebrew,
  onClose,
  focusVerseRef,
  onNavigateToParsha,
}: SefariaReaderModalProps) {
  const { authState } = useAuth();
  const userId = authState.status === 'authenticated' ? authState.user.uid : '';
  const isOnline = useOnlineStatus();

  // Text data
  const { text, loading, error, isFromCache, refresh } = useSefariaText(parshaRef, parshaTitle);

  // Versions
  const [versions, setVersions] = useState<SefariaVersion[]>([]);
  const isMountedRef = useMountedRef();

  // Settings
  const [settings, setSettings] = useState(() => getReaderSettings());
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Highlights
  const [highlightedVerses, setHighlightedVerses] = useState<string[]>(() => getHighlights(userId));

  // Commentary expansion
  const [expandedCommentary, setExpandedCommentary] = useState<string | null>(null);

  // Scroll position
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentVerseIndex, setCurrentVerseIndex] = useState(0);
  // Refs for verse elements to enable scroll-to operations
  const verseRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Focus verse animation (for when opened from favorites)
  const [blinkingVerseRef, setBlinkingVerseRef] = useState<string | null>(null);
  const hasScrolledToFocus = useRef(false);

  // Helper function to get verse element by index (uses refs first, falls back to getElementById)
  const getVerseElement = useCallback((index: number): HTMLElement | null => {
    return verseRefs.current.get(index) || document.getElementById(`verse-${index}`);
  }, []);

  // Toast and visual effects for celebrations
  const { showToast } = useToast();
  const { triggerState } = useVisualEffectsContext();

  // Reading progress tracking
  const { trackCommentaryExpansion, trackHighlight } = useReadingProgress({
    parshaRef,
    parshaTitle,
    parshaHebrew,
    totalVerses: text?.verses.length || 0,
    scrollContainerRef: scrollRef as React.RefObject<HTMLElement>,
    enabled: !!userId && !!text?.verses.length,
    callbacks: {
      onFirstVerse: () => {
        showToast({ message: formatCelebrationMessage('firstVerse', {}), type: 'success' });
      },
      onParshaComplete: (title, isFirstParsha) => {
        triggerState('celebration');
        showToast({
          message: formatCelebrationMessage('parshaComplete', { parsha: title }),
          type: 'success',
        });
        if (isFirstParsha) {
          setTimeout(() => {
            showToast({
              message: formatCelebrationMessage('firstParsha', {}),
              type: 'success',
            });
          }, 2000);
        }
      },
      onStreakUpdate: (newStreak, isNewDay) => {
        if (isNewDay && newStreak > 1) {
          showToast({
            message: formatCelebrationMessage('readingStreak', { days: newStreak }),
            type: 'success',
          });
        }
      },
    },
  });

  // Load versions on mount
  useEffect(() => {
    if (text?.book) {
      getAvailableVersions(text.book)
        .then((v) => {
          if (isMountedRef.current) setVersions(v);
        })
        .catch(console.error);
    }
  }, [text?.book, isMountedRef]);

  // Restore reading position on mount (only if no focusVerseRef)
  useEffect(() => {
    if (!text?.verses.length) return;
    if (focusVerseRef) return; // Skip if we have a specific verse to focus

    const savedPosition = getReadingPosition(parshaRef);
    if (savedPosition) {
      const index = text.verses.findIndex((v) => v.ref === savedPosition);
      if (index > 0) {
        // Scroll to saved position after render
        setTimeout(() => {
          const element = getVerseElement(index);
          element?.scrollIntoView({ behavior: 'auto', block: 'start' });
        }, 100);
      }
    }
  }, [text?.verses, parshaRef, focusVerseRef, getVerseElement]);

  // Scroll to and animate focused verse (when opened from favorites)
  useEffect(() => {
    if (!focusVerseRef || !text?.verses.length || hasScrolledToFocus.current) return;

    // Find the verse index by matching the reference
    const index = text.verses.findIndex((v) => v.ref === focusVerseRef);
    if (index === -1) return;

    hasScrolledToFocus.current = true;

    // Scroll to the verse after a short delay for render
    const startBlinkThenStop = () => {
      setBlinkingVerseRef(focusVerseRef);
      setTimeout(() => setBlinkingVerseRef(null), 3000);
    };
    const scrollAndAnimate = () => {
      const element = getVerseElement(index);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(startBlinkThenStop, 500);
      }
    };
    setTimeout(scrollAndAnimate, 200);
  }, [focusVerseRef, text?.verses, getVerseElement]);

  // Save reading position on close
  useEffect(() => {
    return () => {
      if (text?.verses[currentVerseIndex]) {
        setReadingPosition(parshaRef, text.verses[currentVerseIndex].ref);
      }
    };
  }, [parshaRef, text?.verses, currentVerseIndex]);

  // Lock body overflow
  useBodyOverflowLock(true);

  // Keyboard handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isSettingsOpen) {
          setIsSettingsOpen(false);
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, isSettingsOpen]);

  // Browser back button handler - push state when modal opens, close on popstate
  useEffect(() => {
    // Push a state entry when modal opens
    const stateId = `sefaria-reader-${Date.now()}`;
    let closedViaPopstate = false;

    window.history.pushState({ sefariaReader: stateId }, '');

    const handlePopState = () => {
      // User pressed back button, close the modal
      closedViaPopstate = true;
      onClose();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      // If closing normally (not via back button), pop the history state we added
      if (!closedViaPopstate && window.history.state?.sefariaReader === stateId) {
        window.history.back();
      }
    };
  }, [onClose]);

  // Track scroll position for current verse indicator
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement || !text?.verses.length) return;

    const handleScroll = () => {
      const scrollTop = scrollElement.scrollTop;
      const verseElements = scrollElement.querySelectorAll('[data-verse-index]');

      for (let i = verseElements.length - 1; i >= 0; i--) {
        const element = verseElements[i] as HTMLElement;
        if (element.offsetTop <= scrollTop + 100) {
          setCurrentVerseIndex(parseInt(element.dataset.verseIndex || '0', 10));
          break;
        }
      }
    };

    scrollElement.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, [text?.verses.length]);

  // Handlers
  const handleLayoutChange = useCallback((layout: ReaderLayout) => {
    setSettings((prev) => {
      const updated = { ...prev, layout };
      setReaderSettings(updated);
      return updated;
    });
  }, []);

  const handleFontSizeChange = useCallback((fontSize: ReaderFontSize) => {
    setSettings((prev) => {
      const updated = { ...prev, fontSize };
      setReaderSettings(updated);
      return updated;
    });
  }, []);

  const handleHighlight = useCallback(
    (verseRef: string) => {
      const wasHighlighted = highlightedVerses.includes(verseRef);
      toggleHighlight(userId, verseRef);
      setHighlightedVerses(getHighlights(userId));

      // Track new highlights for reading progress
      if (!wasHighlighted) {
        trackHighlight();
      }
    },
    [userId, highlightedVerses, trackHighlight]
  );

  const handleClearHighlights = useCallback(() => {
    clearHighlights(userId);
    setHighlightedVerses([]);
  }, [userId]);

  const handleClearPosition = useCallback(() => {
    clearReadingPosition(parshaRef);
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [parshaRef]);

  const handleVersionChange = useCallback((version: string) => {
    setSettings((prev) => {
      const updated = { ...prev, selectedVersion: version };
      setReaderSettings(updated);
      return updated;
    });
    // [FUTURE] Refetch with new version
  }, []);

  const handleToggleCommentary = useCallback(
    (verseRef: string) => {
      const wasExpanded = expandedCommentary === verseRef;
      setExpandedCommentary((prev) => (prev === verseRef ? null : verseRef));

      // Track new commentary expansions for reading progress
      if (!wasExpanded) {
        trackCommentaryExpansion();
      }
    },
    [expandedCommentary, trackCommentaryExpansion]
  );

  const scrollToTop = useCallback(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const scrollToBottom = useCallback(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, []);

  // Jump to a verse within the current parsha (from favorites dropdown)
  const handleJumpToVerse = useCallback(
    (verseRef: string) => {
      if (!text?.verses) return;

      const index = text.verses.findIndex((v) => v.ref === verseRef);
      if (index === -1) return;

      // Scroll to the verse
      const element = getVerseElement(index);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Start the blink animation after scroll
        setTimeout(() => {
          setBlinkingVerseRef(verseRef);
          setTimeout(() => {
            setBlinkingVerseRef(null);
          }, 3000);
        }, 500);
      }
    },
    [text?.verses, getVerseElement]
  );

  // Navigate to a different parsha (from favorites dropdown)
  const handleOpenDifferentParsha = useCallback(
    (
      newParshaRef: string,
      newParshaTitle: string,
      newParshaHebrew: string,
      newFocusVerseRef: string
    ) => {
      if (onNavigateToParsha) {
        // Close this modal and let parent handle navigation
        onNavigateToParsha(newParshaRef, newParshaTitle, newParshaHebrew, newFocusVerseRef);
      } else {
        // Fallback: just close the modal (parent doesn't support navigation)
        onClose();
      }
    },
    [onNavigateToParsha, onClose]
  );

  // Render content
  const renderContent = () => {
    if (loading && !text) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-slate-400">
            <Loader size={48} className="animate-spin mx-auto mb-4" />
            <p className="text-lg">Loading Torah text...</p>
            <p className="text-sm">Fetching from Sefaria</p>
          </div>
        </div>
      );
    }

    if (error && !text) {
      return (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center max-w-md">
            <AlertCircle size={48} className="text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-700 mb-2">Failed to Load Text</h3>
            <p className="text-slate-500 mb-4">
              {error.message || 'An error occurred while loading the Torah text.'}
            </p>
            <button
              onClick={refresh}
              className="px-4 py-2 bg-[#8e4585] text-white rounded-lg hover:bg-[#63345e] transition-colors flex items-center gap-2 mx-auto"
            >
              <RefreshCw size={16} />
              Try Again
            </button>
          </div>
        </div>
      );
    }

    if (!text?.verses.length) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-slate-400">No verses found</p>
        </div>
      );
    }

    return (
      <div ref={scrollRef} className="flex-1 overflow-y-auto bg-[#faf8f5]">
        <div className="max-w-3xl mx-auto py-6 px-4 space-y-4">
          {text.verses.map((verse, index) => (
            <div
              key={verse.ref}
              id={`verse-${index}`}
              data-verse-index={index}
              ref={(el) => {
                if (el) {
                  verseRefs.current.set(index, el);
                } else {
                  verseRefs.current.delete(index);
                }
              }}
            >
              <VerseView
                verse={verse}
                layout={settings.layout}
                fontSize={settings.fontSize}
                isHighlighted={highlightedVerses.includes(verse.ref)}
                isCommentaryExpanded={expandedCommentary === verse.ref}
                isBlinking={blinkingVerseRef === verse.ref}
                onHighlight={handleHighlight}
                onToggleCommentary={handleToggleCommentary}
                userId={userId}
                parshaContext={`Parashat ${parshaTitle}`}
                parshaRef={parshaRef}
                parshaHebrew={parshaHebrew}
              />
              <AnimatePresence>
                {expandedCommentary === verse.ref && (
                  <CommentaryPanel
                    verseRef={verse.ref}
                    isExpanded={true}
                    onClose={() => setExpandedCommentary(null)}
                  />
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const modal = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-white flex flex-col"
    >
      {/* Header */}
      <ReaderHeader
        title={parshaTitle}
        hebrewTitle={parshaHebrew}
        currentParshaRef={parshaRef}
        onOpenSettings={() => setIsSettingsOpen(true)}
        onClose={onClose}
        onJumpToVerse={handleJumpToVerse}
        onOpenDifferentParsha={handleOpenDifferentParsha}
      />

      {/* Main content */}
      {renderContent()}

      {/* Footer */}
      {text && (
        <ReaderFooter
          layout={settings.layout}
          fontSize={settings.fontSize}
          currentVerse={currentVerseIndex + 1}
          totalVerses={text.verses.length}
          onLayoutChange={handleLayoutChange}
          onFontSizeChange={handleFontSizeChange}
          onScrollToTop={scrollToTop}
          onScrollToBottom={scrollToBottom}
        />
      )}

      {/* Settings Panel */}
      <ReaderSettingsPanel
        isOpen={isSettingsOpen}
        versions={versions}
        selectedVersion={settings.selectedVersion}
        highlightedCount={highlightedVerses.length}
        isAvailableOffline={isParshaAvailableOffline(parshaRef)}
        isOffline={!isOnline}
        isUsingCache={isFromCache}
        onVersionChange={handleVersionChange}
        onClearHighlights={handleClearHighlights}
        onClearPosition={handleClearPosition}
        onClose={() => setIsSettingsOpen(false)}
      />
    </motion.div>
  );

  return createPortal(modal, document.body);
}
